import Layout from "@/components/layout/Layout";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ReactNode } from "react";

interface LegalPageProps {
  eyebrow: string;
  title: React.ReactNode;
  description: string;
  seoTitle: string;
  seoDescription: string;
  path: string;
  updated: string;
  children: ReactNode;
}

export const LegalPage = ({ eyebrow, title, description, seoTitle, seoDescription, path, updated, children }: LegalPageProps) => {
  const url = `https://cloudease.in${path}`;
  return (
    <Layout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
      </Helmet>

      <section className="relative pt-32 pb-16 overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-aurora animate-aurora opacity-70" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-5">{eyebrow}</p>
            <h1 className="text-4xl md:text-6xl font-heading font-semibold tracking-tight mb-6">{title}</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">{description}</p>
            <p className="mt-6 text-xs font-mono uppercase tracking-widest text-muted-foreground">Last updated: {updated}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto prose-content space-y-10 text-foreground/85 leading-relaxed">
            {children}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export const LegalSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <div>
    <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">{title}</h2>
    <div className="space-y-3 text-foreground/80">{children}</div>
  </div>
);
